// ================================================================
// src/pages/LessonStepsPage.jsx - نسخه نهایی با اشتراک
// ================================================================

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from './PrivateClassShared';
import InlineVideoPlayer from '../components/books/InlineVideoPlayer';
import './LessonStepsPage.css';
import '../components/books/StepQuiz.css';

import { startPayment, PAYMENT_PURPOSE } from '../services/paymentService.js';

import { engine } from '../core/books/storage.js';
import dataEngine from '../core/books/data.js';
import { normalizeGradeId, normalizeSubjectId } from '../core/education/curriculum.js';
import { progressManager, subscriptionManager } from '../core/books/logic.js';

// 🆕 کامپوننت‌های اشتراک
import {
  SubscriptionCard,
  SubscriptionTimer,
  SubscriptionModal,
} from '../components/books/BooksSharedUI.jsx';


// ================================================================
// توابع کمکی
// ================================================================
const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '';
  if (minutes < 60) return `${minutes} دقیقه`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours} ساعت`;
  return `${hours}:${mins.toString().padStart(2, '0')} ساعت`;
};


// ================================================================
// تبدیل داده‌ی داینامیک درس به فرمت سازگار با رندر فعلی
// ================================================================
const normalizeDynamicLesson = async (dynamicLesson) => {
  if (!dynamicLesson || !Array.isArray(dynamicLesson.steps)) return null;

  const steps = await Promise.all(
    dynamicLesson.steps.map(async (st, idx) => {
      const videos = await Promise.all(
        (st.videos || []).map(async (v) => {
          let url = v.externalUrl || null;
          if (!url && v.blobId) {
            try { url = await engine.blobs.getUrl(v.blobId); } catch {}
          }
          return {
            url: url || '',
            title: v.title || '',
            _id: v._id || null,
            _blobId: v.blobId || null,
          };
        })
      );

      return {
        _id: st._id || null,
        _order: Number.isFinite(st.order) ? st.order : idx,
        type: st.type || 'lesson',
        quizId: st.quizId || null,
        quizMeta: st.quizMeta || null,
        title: st.title || '',
        description: st.description || '',
        duration: st.durationMinutes ?? 0,
        videos,
      };
    })
  );

  steps.sort((a, b) => (a._order ?? 0) - (b._order ?? 0));

  let image = null;
  if (dynamicLesson.imageBlobId) {
    try { image = await engine.blobs.getUrl(dynamicLesson.imageBlobId); } catch {}
  }

  return {
    title: dynamicLesson.title || '',
    description: dynamicLesson.description || '',
    image,
    imageBlobId: dynamicLesson.imageBlobId || null,
    isFree: dynamicLesson.isFree === true,   // 🆕 حفظ فیلد رایگان
    steps,
    _isDynamic: true,
  };
};


// ================================================================
// کامپوننت اصلی
// ================================================================
export default function LessonStepsPage() {
  const { grade: rawGrade, subject: rawSubject, folderName, lessonId } = useParams();
  const navigate = useNavigate();

  const grade = normalizeGradeId(rawGrade) || rawGrade;
  const subject = normalizeSubjectId(rawSubject) || rawSubject;
  const { user, updateUser } = useAuth();
  const showToast = useToast();
  const toastRef = useRef(showToast);

  useEffect(() => { toastRef.current = showToast; }, [showToast]);

  // ===== STATEها (ترتیب مهم است) =====
  // 🆕 خواندن از engine (هماهنگ با remoteSync)
  const [isDark, setIsDark] = useState(() => {
    try { return engine.getRaw('navbar2-drawer-dark') === '1'; }
    catch { return false; }
  });

  const [lessonData, setLessonData] = useState(null);
  const [steps, setSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // 🆕 منبع واحد حقیقت: آرایه‌ی stepIds
  const [completedStepIds, setCompletedStepIds] = useState([]);
  const [stepNotes, setStepNotes] = useState({});
  const [hydrated, setHydrated] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [videoToPlay, setVideoToPlay] = useState(null);
  const [lessonImage, setLessonImage] = useState(null);
  const [navigatingToQuiz, setNavigatingToQuiz] = useState(false);
  const [lessonProject, setLessonProject] = useState(null);
  const [progressTick, setProgressTick] = useState(0);

  // 🆕 Stateهای اشتراک
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionMode, setSubscriptionMode] = useState('purchase');
  const [subscriptionTick, setSubscriptionTick] = useState(0);
  const [subscribing, setSubscribing] = useState(false);

  // Refs
  const userTouchedRef = useRef(false);

  // ===== بازگشت ایمن =====
  // اگه کاربر از داخل اپ اومده → برگرد به صفحه‌ی قبلی
  // اگه مستقیم وارد شده → برو به لیست درس‌های همین کتاب (جلوگیری از صفحه‌ی سیاه)
  const handleBack = useCallback(() => {
    if (window.history.length <= 1) {
      navigate(`/book/${grade}/${subject}`, { replace: true });
    } else {
      navigate(-1);
    }
  }, [navigate, grade, subject]);

  // ===== مقادیر مشتق‌شده (وابسته به stateهای بالا) =====
  const userId = user?.id || 'unknown';
  const storageKey = `lesson-progress-${userId}-${grade}-${subject}-${folderName}-${lessonId}`;

  // 🆕 indexها از completedStepIds محاسبه می‌شوند
  const completedSteps = useMemo(() => {
    if (!steps.length) return [];
    const idToIndex = {};
    steps.forEach((st, idx) => { if (st._id) idToIndex[st._id] = idx; });
    return completedStepIds
      .map((id) => idToIndex[id])
      .filter((i) => i !== undefined && i >= 0);
  }, [completedStepIds, steps]);

  // 🆕 وضعیت اشتراک پروژه
  const subscription = useMemo(() => {
    if (!lessonProject?.id || userId === 'unknown') {
      return { status: 'none', daysLeft: 0, expiresAt: null, record: null };
    }
    try {
      return subscriptionManager.getStatus(userId, lessonProject.id);
    } catch {
      return { status: 'none', daysLeft: 0, expiresAt: null, record: null };
    }
  }, [lessonProject?.id, userId, subscriptionTick]);

  // 🆕 دسترسی سطح درس
  const lessonAccess = useMemo(() => {
    if (!lessonProject || !lessonData) {
      return { allowed: true, reason: 'no-project' };
    }
    try {
      return subscriptionManager.checkAccess({
        user,
        project: lessonProject,
        lesson: lessonData,
        userId,
      });
    } catch {
      return { allowed: false, reason: 'error' };
    }
  }, [user, lessonProject, lessonData, userId, subscriptionTick]);


  // ================================================================
  // EFFECTها
  // ================================================================

  // --- تم: ذخیره ---
  useEffect(() => {
    try {
      // 🆕 از engine → remoteSync → userPrefs (سرور)
      engine.setRaw('navbar2-drawer-dark', isDark ? '1' : '0');
    } catch {}
  }, [isDark]);

  // --- تم: گوش دادن به تغییرات ---
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'navbar2-drawer-dark') setIsDark(e.newValue === '1');
    };
    window.addEventListener('storage', handleStorage);
    const interval = setInterval(() => {
      try {
        // 🆕 از engine
        const current = engine.getRaw('navbar2-drawer-dark') === '1';
        setIsDark((prev) => (prev !== current ? current : prev));
      } catch {}
    }, 500);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  // --- 🆕 ۱. Hydration: خواندن از دیتابیس (منبع حقیقت) + localStorage (کش) ---
  useEffect(() => {
    if (userId === 'unknown') {
      setHydrated(true);
      return;
    }

    // 🆕 منبع واحد حقیقت: engine (sync خودکار با سرور از طریق remoteSync)
    let parsed = null;
    try {
      parsed = engine.get(storageKey, null);
    } catch (e) {
      console.warn('[LessonStepsPage] hydration failed:', e);
    }

    if (parsed) {
      if (Array.isArray(parsed.completedStepIds)) {
        setCompletedStepIds(parsed.completedStepIds);
      } else if (Array.isArray(parsed.completedSteps) && steps.length > 0) {
        const ids = parsed.completedSteps
          .map((idx) => steps[idx]?._id)
          .filter(Boolean);
        setCompletedStepIds(ids);
      }
      setStepNotes(parsed.notes || {});
    } else {
      setCompletedStepIds([]);
      setStepNotes({});
    }

    setHydrated(true);
  }, [storageKey, userId, steps.length]);

  // --- 🆕 ۲. Save: ذخیره در localStorage (کش) + دیتابیس (منبع حقیقت) ---
  useEffect(() => {
    if (!hydrated) return;
    if (userId === 'unknown') return;
    if (!steps.length) return;
    if (!userTouchedRef.current) return;

    userTouchedRef.current = false;

    const data = {
      completedStepIds,
      notes: stepNotes,
      _v: '3.0',
      lastVisitedAt: Date.now(),
    };

    // 🆕 ذخیره‌ی واحد از طریق engine → remoteSync → lessonProgress (سرور)
    try {
      engine.set(storageKey, data);
    } catch (e) {
      console.warn('[LessonStepsPage] save failed:', e);
    }

    // 🆕 emit رویداد برای رفرش خودکار books.jsx
    try {
      engine.emit('progress:changed', {
        source: 'lesson',
        userId, grade, subject, folderName, lessonId,
      });
      engine.broadcast('progress:changed', {
        source: 'lesson',
        userId, grade, subject, folderName, lessonId,
      });
    } catch (err) {
      // silent
    }
  }, [completedStepIds, stepNotes, hydrated, storageKey, userId, steps.length]);











  const isLessonFullyComplete = useCallback((lesson, projectId) => {
    if (!lesson) return false;
    const legacyLessonId = lesson.legacyLessonId;
    if (!legacyLessonId) return false;

    const steps = lesson.steps || [];
    if (steps.length === 0) return false;

    const key = `lesson-progress-${userId}-${grade}-${subject}-${folderName}-${legacyLessonId}`;
    let completedStepIds = [];
    try {
      // 🆕 از engine
      const parsed = engine.get(key, null);
      if (parsed && Array.isArray(parsed.completedStepIds)) {
        completedStepIds = parsed.completedStepIds;
      }
    } catch {}

    for (const step of steps) {
      if (step.type === 'quiz') {
        const p = progressManager.getQuizStepProgress(userId, projectId, step._id);
        if (!p?.passed) return false;
      } else {
        if (!completedStepIds.includes(step._id)) return false;
      }
    }
    return true;
  }, [userId, grade, subject, folderName]);













    // ================================================================
  // 🆕 گارد امنیتی: آیا درس قبلی کامل شده؟
  // ================================================================
  const checkPreviousLessonGuard = useCallback((dynamicResult) => {
    try {
      if (!dynamicResult?.lesson || !dynamicResult?.bookData) {
        return { allowed: true };
      }
      if (userId === 'unknown') {
        return { allowed: true }; // کاربر مهمان نمی‌تواند چک شود
      }

      const projectId = dynamicResult.project?.id
        || dynamicResult.project?.projectId
        || dynamicResult.bookData.projectId
        || null;
      if (!projectId) return { allowed: true };

      const chapters = dynamicResult.bookData.chapters || [];
      const currentLessonId = dynamicResult.lesson._id;

      // پیدا کردن موقعیت درس فعلی
      let currentCi = -1;
      let currentLi = -1;
      let prevLesson = null;

      for (let ci = 0; ci < chapters.length; ci++) {
        const lessons = chapters[ci].lessons || [];
        for (let li = 0; li < lessons.length; li++) {
          if (lessons[li]._id === currentLessonId) {
            currentCi = ci;
            currentLi = li;
            break;
          }
          prevLesson = lessons[li];
        }
        if (currentCi >= 0) break;
      }

      // اگر اولین درس کل کتاب است → آزاد
      if (currentCi < 0) return { allowed: true };
      if (currentCi === 0 && currentLi === 0) return { allowed: true };
      if (!prevLesson) return { allowed: true };

      // چک کامل بودن درس قبلی
      const prevComplete = isLessonFullyComplete(prevLesson, projectId);
      return { allowed: prevComplete, prevLesson };
    } catch (e) {
      console.warn('[LessonStepsPage] guard check failed:', e);
      return { allowed: true }; // در صورت خطا، اجازه بده
    }
  }, [userId, grade, subject, folderName]);









  // ================================================================
  // 🆕 چک کامل بودن یک درس (برای گارد)
  // ================================================================












  // --- 🆕 ۳. بارگذاری درس ---
  const loadLesson = useCallback(async () => {
    setIsLoading(true);
    try {
      let data = null;
      let isDynamic = false;

      // ---------- اولویت ۱: registry ----------
      try {
        const dynamicResult = dataEngine.query.getLessonData(
          grade, subject, folderName, lessonId
        );
        if (dynamicResult?.lesson) {
          // 🆕 گارد امنیتی: چک کن درس قبلی کامل شده باشد
          const guardResult = checkPreviousLessonGuard(dynamicResult);
          if (!guardResult.allowed) {
            toastRef.current('🔒 ابتدا درس قبلی را کامل کنید.', 'warning');
            setTimeout(() => handleBack(), 1500);
            setIsLoading(false);
            return;
          }

          const normalized = await normalizeDynamicLesson(dynamicResult.lesson);
          if (normalized && normalized.steps.length > 0) {
            data = normalized;
            isDynamic = true;
            setLessonProject(dynamicResult.project || null);
          }
        }
      } catch (e) {
        console.warn('⚠️ بارگذاری از registry ناموفق:', e);
      }

      // ---------- اولویت ۲: legacy ----------
      if (!data) {
        try {
          const module = await import(
            `../Lessons-Gam/${grade}/${subject}/${folderName}/${lessonId}.jsx`
          );
          const legacy = module.default || module;
          if (legacy?.steps?.length > 0) {
            data = legacy;
            setLessonProject(null);
          }
        } catch (error) {
          console.warn(`درس ${lessonId} یافت نشد:`, error);
        }
      }

      // ---------- اعمال ----------
      if (data?.steps?.length > 0) {
        setLessonData(data);
        setSteps(data.steps);
        setLessonImage(data.image || null);
        setCurrentStepIndex(0);

        // ثبت رکورد درس
        // ثبت رکورد درس — در localStorage (کش) + دیتابیس (منبع حقیقت)
try {
  const recordsKey = `lesson-records-${userId}`;
  let records = {};
  try {
    // 🆕 از engine
    records = engine.get(recordsKey, {}) || {};
  } catch {}

  let teacherName = '';
  if (folderName && folderName !== 'default') {
    try {
      // 🆕 از engine
      const tmap = engine.get(`teacher-map-${userId}`, {}) || {};
      teacherName = tmap[folderName]?.name || '';
    } catch {}
  }

  // 🆕 استخراج متادیتا
  const lessonStepIds = (data.steps || [])
    .filter((s) => s.type !== 'quiz')
    .map((s) => s._id)
    .filter(Boolean);

  const quizStepIds = (data.steps || [])
    .filter((s) => s.type === 'quiz')
    .map((s) => s._id)
    .filter(Boolean);

  const recordProjectId =
    dynamicResult?.project?.id ||
    dynamicResult?.project?.projectId ||
    dynamicResult?.bookData?.projectId ||
    null;

  const recordField =
    dynamicResult?.project?.field ||
    dynamicResult?.bookData?.field ||
    null;

  const compositeId = `${grade}|${subject}|${folderName}|${lessonId}`;
  records[compositeId] = {
    grade,
    subject,
    folderName,
    lessonId,
    lessonTitle: data.title || lessonId,
    image: data.image || '',
    imageBlobId: data.imageBlobId || null,
    totalSteps: data.steps.length,

    // 🆕 فیلدهای جدید
    projectId: recordProjectId,
    field: recordField,
    lessonStepIds,
    quizStepIds,
    totalLessonSteps: lessonStepIds.length,
    totalQuizSteps: quizStepIds.length,

    teacherName,
    isDynamic,
    lastVisited: Date.now(),
    _v: '3.1',
  };

  // 🆕 ذخیره‌ی واحد از طریق engine → remoteSync → lessonRecords (سرور)
  try {
    engine.set(recordsKey, records);
  } catch (e) {
    console.warn('[LessonStepsPage] save records failed:', e);
  }
} catch (e) {
  console.warn('⚠️ ثبت رکورد درس ناموفق:', e);
}
      } else {
        toastRef.current('داده‌های این درس ناقص است.', 'error');
      }
    } catch (error) {
      console.error('❌ خطا در بارگذاری درس:', error);
      toastRef.current('درس یافت نشد.', 'error');
    } finally {
      setIsLoading(false);
    }
    }, [grade, subject, folderName, lessonId, userId, checkPreviousLessonGuard, handleBack]);

  useEffect(() => {
    loadLesson();
  }, [loadLesson]);

  // --- 🆕 ۴. Storage Listener ---
  useEffect(() => {
    if (!storageKey) return;
    if (userId === 'unknown') return;

    const handleStorageEvent = (e) => {
      if (e.key !== storageKey) return;
      if (!e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue);
        if (Array.isArray(parsed.completedStepIds)) {
          setCompletedStepIds(parsed.completedStepIds);
        }
        if (parsed.notes && typeof parsed.notes === 'object') {
          setStepNotes(parsed.notes);
        }
      } catch (err) {
        console.warn('[LessonStepsPage] storage event parse error:', err);
      }
    };

    window.addEventListener('storage', handleStorageEvent);
    return () => window.removeEventListener('storage', handleStorageEvent);
  }, [storageKey, userId]);

  // --- 🆕 ۵. گوش دادن به تغییرات اشتراک ---
  useEffect(() => {
    const handleSubChange = () => setSubscriptionTick((t) => t + 1);
    const unsub1 = engine.on('subscription:changed', handleSubChange);
    const unsub2 = engine.on('sync:subscription:changed', handleSubChange);
    const unsub3 = engine.on('sync:subscription:purchased', handleSubChange);
    return () => {
      try { unsub1(); } catch {}
      try { unsub2(); } catch {}
      try { unsub3(); } catch {}
    };
  }, []);

  // --- 🆕 ۶. گوش دادن به رویدادهای progress ---
  useEffect(() => {
  const reloadCompleted = () => {
    try {
      // 🆕 از engine
      const parsed = engine.get(storageKey, null);
      if (parsed && Array.isArray(parsed.completedStepIds)) {
        setCompletedStepIds(parsed.completedStepIds);
      }
    } catch {}
  };

    const bumpProgress = () => setProgressTick(t => t + 1);
    const fullReload = () => { reloadCompleted(); bumpProgress(); };

    const unsub1 = engine.on('progress:changed', fullReload);
    const unsub2 = engine.on('quiz:step-progress-changed', fullReload);
    const unsub3 = engine.on('quiz:step-passed', fullReload);
    const unsub4 = engine.on('quiz:unlocked', bumpProgress);
    const unsub5 = engine.on('sync:progress:changed', fullReload);
    const unsub6 = engine.on('sync:quiz:step-progress-changed', fullReload);

    return () => {
      try { unsub1(); } catch {}
      try { unsub2(); } catch {}
      try { unsub3(); } catch {}
      try { unsub4(); } catch {}
      try { unsub5(); } catch {}
      try { unsub6(); } catch {}
    };
  }, [steps, storageKey]);


  // ================================================================
  // CALLBACKها
  // ================================================================

  const isStepCompletedById = useCallback((index) => {
    const step = steps[index];
    if (!step) return false;

    if (step.type === 'quiz') {
      const projectId = lessonProject?.id || lessonProject?.projectId || null;
      if (userId !== 'unknown' && projectId) {
        const p = progressManager.getQuizStepProgress(userId, projectId, step._id);
        return !!p?.passed;
      }
      return false;
    }

    return completedSteps.includes(index);
  }, [steps, completedSteps, lessonProject, userId, progressTick]);

  const getFirstIncompleteIndex = useCallback(() => {
    for (let i = 0; i < steps.length; i++) {
      if (!isStepCompletedById(i)) return i;
    }
    return -1;
  }, [steps, isStepCompletedById]);

  const isStepLocked = useCallback((index) => {
    // قفل اشتراک
    if (!lessonAccess.allowed) return true;

    // مرحله‌ی کامل‌شده → باز برای مرور
    if (isStepCompletedById(index)) return false;

    // اولین مرحله → باز
    if (index === 0) return false;

    // قفل زنجیره‌ای
    for (let i = 0; i < index; i++) {
      if (!isStepCompletedById(i)) return true;
    }

    return false;
  }, [isStepCompletedById, lessonAccess.allowed]);

  const isStepCompleted = useCallback((index) => {
    return completedSteps.includes(index);
  }, [completedSteps]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setVideoToPlay(null);
  }, []);

  const markStepCompleted = useCallback((index) => {
    const stepId = steps[index]?._id;
    if (!stepId) return;
    userTouchedRef.current = true;
    setCompletedStepIds((prev) =>
      prev.includes(stepId) ? prev : [...prev, stepId]
    );
  }, [steps]);

  // 🆕 باز کردن مودال اشتراک — قبل از openStepModal تعریف می‌شود
  const openSubscriptionModal = useCallback((mode = 'purchase') => {
    if (userId === 'unknown') {
      toastRef.current('برای خرید اشتراک، ابتدا وارد شوید.', 'warning');
      navigate('/login');
      return;
    }
    setSubscriptionMode(mode);
    setShowSubscriptionModal(true);
  }, [userId, navigate]);

  // 🆕 تایید خرید/تمدید
const handleSubscriptionConfirm = useCallback(async () => {
    // ── اعتبارسنجی ──
    if (userId === 'unknown') {
      toastRef.current('برای خرید اشتراک ابتدا وارد شوید.', 'warning');
      return;
    }
    if (!lessonProject?.id) {
      toastRef.current('اطلاعات پروژه ناقص است.', 'error');
      return;
    }

    setSubscribing(true);
    try {
      await startPayment({
        userId: userId,
        purpose: PAYMENT_PURPOSE.LESSON_SUBSCRIPTION,
        metadata: {
          projectId: lessonProject.id,
          teacherId: lessonProject.teacherId || null,
        },
        description: subscription.status === 'active' || subscription.status === 'expired'
          ? 'تمدید اشتراک درس'
          : 'خرید اشتراک درس',
      });
      // کاربر به درگاه هدایت می‌شود
    } catch (err) {
      console.error('❌ پرداخت ناموفق:', err);
      toastRef.current(err?.message || 'خطا در شروع پرداخت', 'error');
      setSubscribing(false);
    }
  }, [lessonProject?.id, lessonProject?.teacherId, userId, subscription.status]);

  const completeCurrentStep = useCallback(() => {
    const currentStep = steps[currentStepIndex];

    if (currentStep?.type === 'quiz') {
      if (!currentStep.quizId) {
        toastRef.current('این آزمون هنوز ساخته نشده است.', 'warning');
        return;
      }
      closeModal();
      setNavigatingToQuiz(true);
      setTimeout(() => {
        navigate(`/student/step-quiz/${grade}/${subject}/${folderName}/${lessonId}/${currentStep._id}`);
      }, 1000);
      return;
    }

    if (completedSteps.includes(currentStepIndex)) return;

    markStepCompleted(currentStepIndex);
    toastRef.current(`✅ مرحله ${currentStepIndex + 1} کامل شد!`, 'success');

    if (currentStepIndex >= steps.length - 1) {
      toastRef.current('🎉 تبریک! همه مراحل کامل شد!', 'success');
      setTimeout(() => {
        setIsModalOpen(false);
        handleBack();
      }, 2000);
      return;
    }

    setTimeout(() => {
      setCurrentStepIndex(prev => prev + 1);
    }, 800);
  }, [
    currentStepIndex, completedSteps, steps, navigate,
    closeModal, markStepCompleted, grade, subject, folderName, lessonId,
    handleBack,
  ]);

  const openStepModal = useCallback((index) => {
    // اول قفل اشتراک
    if (!lessonAccess.allowed) {
      openSubscriptionModal(
        subscription.status === 'expired' ? 'renew' : 'purchase'
      );
      return;
    }

    if (isStepLocked(index)) {
      toastRef.current('ابتدا مرحله قبلی را کامل کنید.', 'warning');
      return;
    }

    const step = steps[index];

    // آزمون → با تأخیر به StepQuizRunner
    if (step?.type === 'quiz') {
      if (!step.quizId) {
        toastRef.current('این آزمون هنوز ساخته نشده است.', 'warning');
        return;
      }

      const projectId = lessonProject?.id || lessonProject?.projectId || null;
      if (userId !== 'unknown' && projectId) {
        const progress = progressManager.getQuizStepProgress(userId, projectId, step._id);
        if (progress?.passed) {
          navigate(`/student/step-quiz/${grade}/${subject}/${folderName}/${lessonId}/${step._id}`);
          return;
        }
      }

      setNavigatingToQuiz(true);
      setTimeout(() => {
        navigate(`/student/step-quiz/${grade}/${subject}/${folderName}/${lessonId}/${step._id}`);
      }, 1000);
      return;
    }

    // مرحله‌ی آموزشی
    setCurrentStepIndex(index);
    setIsModalOpen(true);
  }, [
    isStepLocked, steps, navigate, grade, subject, folderName, lessonId,
    lessonProject, userId,
    lessonAccess.allowed,
    openSubscriptionModal,
    subscription.status,
  ]);

  const goToNextStep = useCallback(() => {
    const currentStep = steps[currentStepIndex];

    // اگر فعلی آزمون است → برو به آزمون
    if (currentStep?.type === 'quiz') {
      if (!currentStep.quizId) {
        toastRef.current('این آزمون هنوز ساخته نشده است.', 'warning');
        return;
      }
      closeModal();
      setNavigatingToQuiz(true);
      setTimeout(() => {
        navigate(`/student/step-quiz/${grade}/${subject}/${folderName}/${lessonId}/${currentStep._id}`);
      }, 1000);
      return;
    }

    // آخرین مرحله
    if (currentStepIndex >= steps.length - 1) {
      toastRef.current('شما در آخرین مرحله هستید.', 'info');
      return;
    }

    // فعلی کامل نشده
    if (!isStepCompleted(currentStepIndex)) {
      completeCurrentStep();
      return;
    }

    // مرحله‌ی ناتمام قبل از current
    const firstIncomplete = getFirstIncompleteIndex();
    if (firstIncomplete >= 0 && firstIncomplete < currentStepIndex) {
      toastRef.current(
        `⚠️ یک مرحله‌ی جدید اضافه شده. ابتدا مرحله‌ی ${firstIncomplete + 1} را کامل کنید.`,
        'warning'
      );

      closeModal();
      setTimeout(() => {
        setCurrentStepIndex(firstIncomplete);
        const targetStep = steps[firstIncomplete];
        if (targetStep?.type === 'quiz' && targetStep.quizId) {
          setNavigatingToQuiz(true);
          setTimeout(() => {
            navigate(`/student/step-quiz/${grade}/${subject}/${folderName}/${lessonId}/${targetStep._id}`);
          }, 800);
        } else {
          setIsModalOpen(true);
        }
      }, 400);
      return;
    }

    // قفل مرحله‌ی بعدی
    const nextIndex = currentStepIndex + 1;
    if (isStepLocked(nextIndex)) {
      if (firstIncomplete >= 0) {
        toastRef.current(
          `⚠️ ابتدا مرحله‌ی ${firstIncomplete + 1} را کامل کنید.`,
          'warning'
        );
      } else {
        toastRef.current('ابتدا مرحله قبلی را کامل کنید.', 'warning');
      }
      return;
    }

    // مرحله‌ی بعدی آزمون
    const nextStep = steps[nextIndex];
    if (nextStep?.type === 'quiz') {
      if (!nextStep.quizId) {
        toastRef.current('این آزمون هنوز ساخته نشده است.', 'warning');
        return;
      }
      closeModal();
      setNavigatingToQuiz(true);
      setTimeout(() => {
        navigate(`/student/step-quiz/${grade}/${subject}/${folderName}/${lessonId}/${nextStep._id}`);
      }, 1000);
      return;
    }

    // مرحله‌ی بعدی آموزشی
    setCurrentStepIndex(nextIndex);
  }, [
    currentStepIndex, steps, completedSteps, completeCurrentStep, isStepLocked,
    closeModal, navigate, grade, subject, folderName, lessonId,
    getFirstIncompleteIndex,
  ]);

  const goToPrevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    } else {
      toastRef.current('شما در اولین مرحله هستید.', 'info');
    }
  }, [currentStepIndex]);

  const saveNote = useCallback((index, text) => {
    userTouchedRef.current = true;
    setStepNotes(prev => ({ ...prev, [index]: text }));
  }, []);

  const playVideo = useCallback((videoUrl) => {
    setVideoToPlay(videoUrl);
  }, []);

  // ================================================================
  // رندر آیتم مرحله
  // ================================================================
  const renderStepItem = useCallback((step, index) => {
    const locked = isStepLocked(index);
    const isQuiz = step.type === 'quiz';
    const subLocked = !lessonAccess.allowed;

    // ─── آزمون مرحله ───
    if (isQuiz) {
      const projectId = lessonProject?.id || lessonProject?.projectId || null;
      const progress = (userId !== 'unknown' && projectId)
        ? progressManager.getQuizStepProgress(userId, projectId, step._id)
        : null;

      const isPassed = !!progress?.passed;
      const attemptsCount = progress?.attempts?.length || 0;
      const maxAttempts = step.quizMeta?.maxAttempts ?? 2;
      const unlockedCount = progress?.unlockedWithPayment?.length || 0;
      const totalAllowed = maxAttempts + unlockedCount;
      const needsPayment = !isPassed && attemptsCount >= totalAllowed;

      const isCurrent = getFirstIncompleteIndex() === index;

      let statusClass = 'unlocked';
      let badgeText = 'شروع آزمون';
      let statusIcon = <i className="fas fa-clipboard-check step-unlocked-icon" />;

      if (isPassed) {
        statusClass = 'completed';
        badgeText = `قبول (${progress.bestScore || 0})`;
        statusIcon = <i className="fas fa-check-circle step-complete-icon" />;
      } else if (locked) {
        statusClass = 'locked';
        badgeText = 'قفل';
        statusIcon = <i className="fas fa-lock step-lock-icon" />;
      } else if (needsPayment) {
        statusClass = 'locked';
        badgeText = 'نیاز به پرداخت';
        statusIcon = <i className="fas fa-lock step-lock-icon" />;
      } else if (attemptsCount > 0) {
        statusClass = 'unlocked';
        badgeText = `تلاش ${attemptsCount}/${totalAllowed}`;
        statusIcon = <i className="fas fa-redo step-unlocked-icon" />;
      } else if (isCurrent) {
        statusClass = 'unlocked current';
        badgeText = 'شروع کن';
        statusIcon = <i className="fas fa-arrow-circle-left step-unlocked-icon" />;
      }

      return (
        <div
          key={index}
          className={`step-item step-item-quiz ${statusClass} ${subLocked ? 'step-sub-locked' : ''}`}
          onClick={() => openStepModal(index)}
        >
          <div className="step-number">
            {subLocked ? '🔒' : index + 1}
          </div>
          <div className="step-content">
            <div className="step-title">
              {step.title}
              <span className="step-quiz-badge">📝 آزمون</span>
            </div>
            <div className="step-quiz-meta">
              <span className="step-quiz-meta-item">
                📊 {step.quizMeta?.questionCount || 0} سوال
              </span>
              <span className="step-quiz-meta-item">
                ⏱️ {step.quizMeta?.duration || 0} دقیقه
              </span>
              <span className="step-quiz-meta-item">
                🎯 قبولی: {step.quizMeta?.passThreshold || 12}/۲۰
              </span>
            </div>
          </div>
          <div className="step-status">
            {subLocked ? (
              <span className="bks-step-sub-badge">🔒 نیاز به اشتراک</span>
            ) : (
              <>
                {statusIcon}
                <span className={`step-badge ${statusClass}-badge`}>{badgeText}</span>
              </>
            )}
          </div>
        </div>
      );
    }

    // ─── مرحله‌ی آموزشی ───
    const completed = isStepCompleted(index);
    const current = index === currentStepIndex;
    const hasNote = stepNotes[index]?.trim().length > 0;
    const isCurrent = getFirstIncompleteIndex() === index;

    let statusIcon, statusClass = '';
    if (completed) {
      statusIcon = <i className="fas fa-check-circle step-complete-icon" />;
      statusClass = 'completed';
    } else if (locked) {
      statusIcon = <i className="fas fa-lock step-lock-icon" />;
      statusClass = 'locked';
    } else if (isCurrent) {
      statusIcon = <i className="fas fa-arrow-circle-left step-unlocked-icon" />;
      statusClass = 'unlocked current';
    } else {
      statusIcon = <i className="fas fa-play-circle step-unlocked-icon" />;
      statusClass = 'unlocked';
    }

    return (
      <div
        key={index}
        className={`step-item ${statusClass} ${current ? 'current' : ''} ${subLocked ? 'step-sub-locked' : ''}`}
        onClick={() => openStepModal(index)}
      >
        <div className="step-number">{subLocked ? '🔒' : index + 1}</div>
        <div className="step-content">
          <div className="step-title">
            {step.title}
            {hasNote && <span className="note-indicator">📝</span>}
          </div>
          {step.description && <div className="step-description">{step.description}</div>}
          {step.duration && (
            <div className="step-duration">
              <i className="fas fa-clock" /> {formatDuration(step.duration)}
            </div>
          )}
        </div>
        <div className="step-status">
          {subLocked ? (
            <span className="bks-step-sub-badge">🔒 نیاز به اشتراک</span>
          ) : (
            <>
              {statusIcon}
              {completed && <span className="step-badge completed-badge">انجام شد</span>}
              {locked && <span className="step-badge locked-badge">قفل</span>}
              {!locked && !completed && <span className="step-badge unlocked-badge">شروع</span>}
            </>
          )}
        </div>
      </div>
    );
  }, [
    isStepLocked,
    isStepCompleted,
    isStepCompletedById,
    getFirstIncompleteIndex,
    currentStepIndex,
    openStepModal,
    stepNotes,
    userId,
    lessonProject,
    lessonAccess.allowed,
    progressTick,
  ]);

  // ================================================================
  // رندر مودال درس
  // ================================================================
  const renderModalContent = useCallback(() => {
    if (!steps.length) return null;
    const step = steps[currentStepIndex];
    if (!step) return null;

    // ─── آزمون ───
    if (step.type === 'quiz') {
      const projectId = lessonProject?.id || lessonProject?.projectId || null;
      const progress = (userId !== 'unknown' && projectId)
        ? progressManager.getQuizStepProgress(userId, projectId, step._id)
        : null;

      const isPassed = !!progress?.passed;
      const attemptsCount = progress?.attempts?.length || 0;
      const maxAttempts = step.quizMeta?.maxAttempts ?? 2;
      const unlockedCount = progress?.unlockedWithPayment?.length || 0;
      const totalAllowed = maxAttempts + unlockedCount;

      return (
        <div className="step-modal-content">
          <div className="step-modal-header">
            <button className="modal-close-btn" onClick={closeModal}>
              <i className="fas fa-times" />
            </button>
            <div className="modal-step-info">
              <h2 className="modal-step-title">{step.title}</h2>
            </div>
          </div>

          <div className="modal-divider" />

          <div className="step-quiz-redirect-card">
            <div className="step-quiz-redirect-icon">
              {isPassed ? '✅' : '📝'}
            </div>
            <h3 className="step-quiz-redirect-title">
              {isPassed ? 'قبولی در آزمون' : 'این مرحله یک آزمون است'}
            </h3>
            <p className="step-quiz-redirect-desc">
              {isPassed
                ? `شما در این آزمون قبول شده‌اید. بهترین نمره: ${progress.bestScore} از ۲۰`
                : 'برای تکمیل این مرحله، باید در آزمون شرکت کنید.'}
            </p>

            {!isPassed && attemptsCount > 0 && (
              <p className="step-quiz-redirect-attempts">
                تلاش‌های انجام‌شده: {attemptsCount} از {totalAllowed}
              </p>
            )}

            <button
              className="step-quiz-redirect-btn"
              onClick={() => {
                closeModal();
                setNavigatingToQuiz(true);
                setTimeout(() => {
                  navigate(`/student/step-quiz/${grade}/${subject}/${folderName}/${lessonId}/${step._id}`);
                }, 1000);
              }}
            >
              {isPassed ? '👁️ مشاهده‌ی کارنامه' : '🚀 شروع آزمون'}
            </button>
          </div>

          <div className="modal-floating-actions">
            <button
              className="floating-btn prev-btn"
              onClick={goToPrevStep}
              disabled={currentStepIndex === 0}
            >
              <i className="fas fa-chevron-right" /> گام قبلی
            </button>
          </div>
        </div>
      );
    }

    // ─── مرحله‌ی آموزشی ───
    const completed = isStepCompleted(currentStepIndex);
    const hasVideos = step.videos?.length > 0;

    return (
      <div className="step-modal-content">
        <div className="step-modal-header">
          <button className="modal-close-btn" onClick={closeModal}><i className="fas fa-times" /></button>
          <div className="modal-step-info">
            <h2 className="modal-step-title">{step.title}</h2>
            {step.description && <p className="modal-step-description">{step.description}</p>}
            {step.duration && <div className="modal-step-duration"><i className="fas fa-hourglass-half" /> زمان: {formatDuration(step.duration)}</div>}
          </div>
        </div>
        <div className="modal-divider" />
        {hasVideos && (
  <div className="modal-videos-section">
    <div className="video-wrapper">
      <InlineVideoPlayer
        key={videoToPlay || step.videos[0]?.url || 'no-video'}
        src={videoToPlay || step.videos[0]?.url}
      />
    </div>
    {step.videos.length > 1 && (
      <div className="videos-grid">
        {step.videos.map((video, idx) => {
          const isActive = (videoToPlay || step.videos[0]?.url) === video.url;
          return (
            <div
              key={idx}
              className={`video-card-mini ${isActive ? 'is-active' : ''}`}
              onClick={() => setVideoToPlay(video.url)}
            >
              <div className="video-card-mini-icon"><i className="fas fa-play-circle" /></div>
              <span className="video-card-mini-title">{video.title || `ویدیو ${idx + 1}`}</span>
            </div>
          );
        })}
      </div>
    )}
  </div>
)}
        <div className="modal-divider" />
        <div className="modal-notes-section">
          <label htmlFor="step-note"><i className="fas fa-pencil-alt" /> یادداشت‌های من {stepNotes[currentStepIndex]?.trim().length > 0 && <span className="saved-indicator">✓ ذخیره شد</span>}</label>
          <textarea id="step-note" className="step-note-textarea" value={stepNotes[currentStepIndex] || ''} onChange={(e) => saveNote(currentStepIndex, e.target.value)} placeholder="یادداشت‌های خود را اینجا بنویسید..." rows={3} />
          <div className="note-char-count">{stepNotes[currentStepIndex]?.length || 0} کاراکتر</div>
        </div>
        <div className="modal-floating-actions">
          <button className="floating-btn prev-btn" onClick={goToPrevStep} disabled={currentStepIndex === 0}><i className="fas fa-chevron-right" /> گام قبلی</button>
          {!completed ? (
            <button className="floating-btn complete-btn" onClick={completeCurrentStep}><i className="fas fa-check" /> اتمام مرحله</button>
          ) : (
            <button className="floating-btn next-btn" onClick={goToNextStep} disabled={currentStepIndex === steps.length - 1}>گام بعدی <i className="fas fa-chevron-left" /></button>
          )}
        </div>
      </div>
    );
  }, [
    steps, currentStepIndex, isStepCompleted, closeModal, videoToPlay, playVideo,
    stepNotes, saveNote, goToPrevStep, goToNextStep, completeCurrentStep,
    navigate, grade, subject, folderName, lessonId, lessonProject, userId,
    lessonAccess.allowed,
  ]);


  // ================================================================
  // رندر نهایی
  // ================================================================

  if (isLoading) {
    return (
      <div className={`lesson-steps-loading ${isDark ? 'dark-mode' : ''}`}>
        <div className="loading-spinner" />
        <span>در حال بارگذاری درس...</span>
      </div>
    );
  }

  if (!lessonData || steps.length === 0) {
    return (
      <div className={`lesson-steps-error ${isDark ? 'dark-mode' : ''}`}>
        <div className="error-icon">📚</div>
        <h2>درس یافت نشد</h2>
        <p>متأسفیم، اطلاعات این درس در دسترس نیست.</p>
        <button className="error-back-btn" onClick={handleBack}>بازگشت</button>
      </div>
    );
  }

  const showSubscriptionUI =
    lessonProject &&
    lessonProject.subscriptionEnabled &&
    Number(lessonProject.subscriptionPrice) > 0;

  return (
    <div className={`lesson-steps-page ${isDark ? 'dark-mode' : ''}`}>
      <header className="lesson-steps-header">
        <div className="header-left">
          <button className="header-back-btn" onClick={handleBack}>
            <i className="fas fa-arrow-right" />
          </button>
        </div>
        <div className="header-center" />
        <div className="header-right">
          <Link to="/home" className="header-home-btn">
            <i className="fas fa-home" />
          </Link>
        </div>
      </header>

      {/* کارت درس */}
      <div className="lesson-hero-card">
        <div className="lesson-hero-image-wrapper">
          {lessonImage
            ? <img src={lessonImage} alt={lessonData.title} className="lesson-hero-image" />
            : <div className="lesson-hero-image-placeholder"><span>📘</span></div>}
        </div>
        <div className="lesson-hero-text">
          <h1 className="lesson-hero-title">{lessonData.title}</h1>
          {lessonData.description && <p className="lesson-hero-description">{lessonData.description}</p>}
        </div>
      </div>

      {/* 🆕 تایمر اشتراک — شناور گوشه‌ی صفحه */}
      {showSubscriptionUI && subscription.status === 'active' && (
        <SubscriptionTimer
          daysLeft={subscription.daysLeft}
          status="active"
          onRenew={() => openSubscriptionModal('renew')}
        />
      )}

      <div className="lesson-divider"><div className="divider-line" /></div>

      {/* 🆕 کارت اشتراک — اگر دسترسی ندارد */}
      {showSubscriptionUI && !lessonAccess.allowed && (
        <SubscriptionCard
          state={subscription.status === 'expired' ? 'expired' : 'locked'}
          price={Number(lessonProject.subscriptionPrice) || 0}
          durationDays={Number(lessonProject.subscriptionDurationDays) || 365}
          daysLeft={subscription.daysLeft || 0}
          onSubscribe={() => openSubscriptionModal('purchase')}
          onRenew={() => openSubscriptionModal('renew')}
          loading={subscribing}
        />
      )}

      {/* 🆕 برچسب درس رایگان */}
      {showSubscriptionUI && lessonData?.isFree && (
        <div
          className="lesson-subscription-lock-banner"
          style={{
            background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
            color: '#065f46',
            borderColor: '#6ee7b7',
          }}
        >
          <span className="lesson-subscription-lock-banner-icon">🎁</span>
          <span>این درس برای همه رایگان است</span>
        </div>
      )}

      {/* 🆕 بنر قفل کل درس */}
      {showSubscriptionUI && !lessonAccess.allowed && subscription.status !== 'active' && (
        <div className="lesson-subscription-lock-banner">
          <span className="lesson-subscription-lock-banner-icon">🔒</span>
          <span>برای دسترسی به مراحل این درس، لطفاً اشتراک تهیه کنید.</span>
        </div>
      )}

      {/* مراحل */}
      <div className="steps-section">
        <div className="steps-section-title">
          <span className="steps-icon">📖</span> مراحل آموزشی:
        </div>
        <div className="steps-list">
          {steps.map((step, index) => renderStepItem(step, index))}
        </div>
      </div>

      {/* Overlay انتقال به آزمون */}
      {navigatingToQuiz && (
        <div className="quiz-navigating-overlay">
          <div className="quiz-navigating-card">
            <div className="quiz-navigating-spinner" />
            <div className="quiz-navigating-text">در حال ورود به آزمون...</div>
            <div className="quiz-navigating-hint">لطفاً صبر کنید</div>
          </div>
        </div>
      )}

      {/* مودال درس */}
      {isModalOpen && (
        <div className="step-modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="step-modal-container">{renderModalContent()}</div>
        </div>
      )}



      {/* 🆕 مودال اشتراک */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => !subscribing && setShowSubscriptionModal(false)}
        project={{
          subscriptionPrice: Number(lessonProject?.subscriptionPrice) || 0,
          subscriptionDurationDays: Number(lessonProject?.subscriptionDurationDays) || 365,
        }}
        mode={subscriptionMode}
        loading={subscribing}
        onConfirm={handleSubscriptionConfirm}
      />
    </div>
  );
}